import { MessageType } from "../store/types";
import { tryParse } from "./object";

/**
 * 检查文件名是否为图片类型
 * @param {string} fileName 文件名（可包含路径）
 * @returns {boolean} 是否为图片
 */
export function isImageFile(fileName: string): boolean {
  // 支持的图片扩展名列表（全小写）
  const imageExtensions = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "webp",
    "tiff",
    "svg",
    "ico",
    "jfif",
  ];

  // 处理空值
  if (!fileName || typeof fileName !== "string") return false;

  // 提取扩展名
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex === -1 || lastDotIndex === fileName.length - 1) {
    return false; // 无扩展名或点号在末尾
  }

  // 获取扩展名并转为小写
  const extension = fileName.slice(lastDotIndex + 1).toLowerCase();

  return imageExtensions.includes(extension);
}

/**
 * 检查文件名是否为视频类型
 * @param {string} fileName 文件名（可包含路径）
 * @returns {boolean} 是否为视频文件
 */
export function isVideoFile(fileName: string): boolean {
  // 支持的视频扩展名列表（全小写）
  const videoExtensions = [
    "mp4",
    "avi",
    "mov",
    "wmv",
    "flv",
    "mkv",
    "webm",
    "mpeg",
    "mpg",
    "m4v",
    "3gp",
    "ogv",
    "ts",
    "m2ts",
    "hevc",
  ];

  // 处理空值
  if (!fileName || typeof fileName !== "string") return false;

  // 清除可能的URL参数
  const cleanName = fileName.split("?")[0];

  // 提取扩展名
  const lastDotIndex = cleanName.lastIndexOf(".");
  if (lastDotIndex === -1 || lastDotIndex === cleanName.length - 1) {
    return false; // 无扩展名或点号在末尾
  }

  // 获取扩展名并转为小写
  const extension = cleanName.slice(lastDotIndex + 1).toLowerCase();

  return videoExtensions.includes(extension);
}

/**
 * 检查文件名是否为音频类型
 * @param {string} fileName 文件名（可包含路径）
 * @returns {boolean} 是否为音频文件
 */
export function isAudioFile(fileName: string): boolean {
  // 支持的音频扩展名列表（全小写）
  const audioExtensions = [
    "mp3",
    "wav",
    "ogg",
    "aac",
    "flac",
    "m4a",
    "wma",
    "aiff",
    "opus",
    "amr",
    "mid",
    "midi",
    "aif",
    "aifc",
    "mka",
    "ra",
    "rm",
    "alac",
    "ac3",
    "dsd",
    "pcm",
    "wv",
    "m4b",
    "m4p",
    "aax",
  ];

  // 处理空值
  if (!fileName || typeof fileName !== "string") return false;

  // 清除可能的URL参数
  const cleanName = fileName.split("?")[0];

  // 提取扩展名
  const lastDotIndex = cleanName.lastIndexOf(".");
  if (lastDotIndex === -1 || lastDotIndex === cleanName.length - 1) {
    return false; // 无扩展名或点号在末尾
  }

  // 获取扩展名并转为小写
  const extension = cleanName.slice(lastDotIndex + 1).toLowerCase();

  return audioExtensions.includes(extension);
}

export const parseFileData = (str: string) => {
  return tryParse(str);
};

export const getFileType = (fileName: string) => {
  const isImage = isImageFile(fileName);
  const isVideo = isVideoFile(fileName);
  const isAudio = isAudioFile(fileName);
  let type = MessageType.File;
  if (isImage || isVideo || isAudio) {
    if (isVideo) {
      type = MessageType.Video;
    }
    if (isAudio) {
      type = MessageType.Audio;
    }
    if (isImage) {
      type = MessageType.Image;
    }
  }
  return type;
};

function u8(buf: ArrayBuffer): Uint8Array {
  return new Uint8Array(buf);
}

/** ISO BMFF（MP4/M4A 等）前若干字节内是否出现 M4A 兼容品牌（部分 m4a 仅标 isom） */
function isoBmffContainsM4ABrand(buf: ArrayBuffer): boolean {
  const a = u8(buf);
  const max = Math.min(a.length, 16384);
  for (let i = 0; i <= max - 4; i++) {
    if (
      a[i] === 0x4d &&
      a[i + 1] === 0x34 &&
      a[i + 2] === 0x41 &&
      a[i + 3] === 0x20
    ) {
      return true;
    }
  }
  return false;
}

/**
 * 根据文件头魔数推断 MessageType；无法识别时返回 null，由调用方回退到扩展名。
 * 不依赖 File.type（可被错误后缀误导）。
 */
export function sniffMessageTypeFromBuffer(
  buf: ArrayBuffer,
): MessageType | null {
  const a = u8(buf);
  if (a.length < 12) {
    return null;
  }

  // —— 图片 ——
  if (a[0] === 0xff && a[1] === 0xd8 && a[2] === 0xff) {
    return MessageType.Image;
  }
  if (
    a[0] === 0x89 &&
    a[1] === 0x50 &&
    a[2] === 0x4e &&
    a[3] === 0x47 &&
    a[4] === 0x0d &&
    a[5] === 0x0a &&
    a[6] === 0x1a &&
    a[7] === 0x0a
  ) {
    return MessageType.Image;
  }
  if (a[0] === 0x47 && a[1] === 0x49 && a[2] === 0x46 && a[3] === 0x38) {
    return MessageType.Image;
  }
  if (
    a.length >= 12 &&
    a[0] === 0x52 &&
    a[1] === 0x49 &&
    a[2] === 0x46 &&
    a[3] === 0x46 &&
    a[8] === 0x57 &&
    a[9] === 0x45 &&
    a[10] === 0x42 &&
    a[11] === 0x50
  ) {
    return MessageType.Image;
  }
  if (a[0] === 0x42 && a[1] === 0x4d) {
    return MessageType.Image;
  }
  // TIFF little / big endian
  if (
    (a[0] === 0x49 && a[1] === 0x49 && a[2] === 0x2a && a[3] === 0x00) ||
    (a[0] === 0x4d && a[1] === 0x4d && a[2] === 0x00 && a[3] === 0x2a)
  ) {
    return MessageType.Image;
  }

  // —— 音频（裸流 / 常见容器）——
  if (a[0] === 0x49 && a[1] === 0x44 && a[2] === 0x33) {
    return MessageType.Audio;
  }
  if (a[0] === 0xff && (a[1] & 0xe0) === 0xe0) {
    return MessageType.Audio;
  }
  if (a[0] === 0x4f && a[1] === 0x67 && a[2] === 0x67 && a[3] === 0x53) {
    return MessageType.Audio;
  }
  if (a[0] === 0x66 && a[1] === 0x4c && a[2] === 0x61 && a[3] === 0x43) {
    return MessageType.Audio;
  }
  if (
    a.length >= 12 &&
    a[0] === 0x52 &&
    a[1] === 0x49 &&
    a[2] === 0x46 &&
    a[3] === 0x46 &&
    a[8] === 0x57 &&
    a[9] === 0x41 &&
    a[10] === 0x56 &&
    a[11] === 0x45
  ) {
    return MessageType.Audio;
  }
  // AIFF: FORM .... AIFF
  if (
    a.length >= 12 &&
    a[0] === 0x46 &&
    a[1] === 0x4f &&
    a[2] === 0x52 &&
    a[3] === 0x4d &&
    a[8] === 0x41 &&
    a[9] === 0x49 &&
    a[10] === 0x46 &&
    a[11] === 0x46
  ) {
    return MessageType.Audio;
  }

  // —— Matroska / WebM ——
  if (a[0] === 0x1a && a[1] === 0x45 && a[2] === 0xdf && a[3] === 0xa3) {
    return MessageType.Video;
  }

  // —— AVI ——
  if (
    a.length >= 12 &&
    a[0] === 0x52 &&
    a[1] === 0x49 &&
    a[2] === 0x46 &&
    a[3] === 0x46 &&
    a[8] === 0x41 &&
    a[9] === 0x56 &&
    a[10] === 0x49
  ) {
    return MessageType.Video;
  }

  // —— ISO BMFF（MP4 / M4A / MOV 等）：offset 4 为 ftyp ——
  if (a[4] === 0x66 && a[5] === 0x74 && a[6] === 0x79 && a[7] === 0x70) {
    const brand = String.fromCharCode(a[8], a[9], a[10], a[11]);
    if (brand === "M4A " || brand === "M4B " || brand === "M4P ") {
      return MessageType.Audio;
    }
    if (brand === "qt  ") {
      return MessageType.Video;
    }
    if (
      brand === "mp42" ||
      brand === "avc1" ||
      brand === "MSNV" ||
      brand === "dash"
    ) {
      return MessageType.Video;
    }
    if (brand === "isom" || brand === "mp41") {
      return isoBmffContainsM4ABrand(buf)
        ? MessageType.Audio
        : MessageType.Video;
    }
    return MessageType.Video;
  }

  return null;
}

const SNIFF_BYTES = 65536;

/**
 * 优先根据文件内容魔数推断类型；失败时回退到扩展名（与 getFileType）。
 */
export async function getFileTypeFromFile(file: File): Promise<MessageType> {
  const isImage = isImageFile(file.name);
  // 只有image才进行魔数推断
  if (!isImage) {
    return getFileType(file.name);
  }
  try {
    const n = Math.min(file.size, SNIFF_BYTES);
    if (n === 0) {
      return getFileType(file.name);
    }
    const buf = await file.slice(0, n).arrayBuffer();
    const sniffed = sniffMessageTypeFromBuffer(buf);
    if (sniffed !== null) {
      return sniffed;
    }
  } catch {
    // ignore
  }
  return getFileType(file.name);
}
